// src/components/Dashboard_ADM/Atendimento/DisparosTab.tsx
// Disparos em massa com template aprovado pela Meta — seção 8.3
// GET /whatsapp-agent/templates | GET/POST /whatsapp-agent/disparos

import React, { useState, useEffect, useCallback } from 'react';
import { Send, Loader2, RefreshCw, AlertCircle, CheckCircle, XCircle } from 'lucide-react';
import apiClient from '../../../services/api';

interface WhatsappTemplate {
  name: string;
  languageCode: string;
  description: string;
}

interface Dispatch {
  id: string;
  templateName: string;
  recipients: string[];
  sentCount: number;
  failedCount: number;
  status: 'concluido' | 'parcial' | 'falhou';
  createdAt: string;
}

const STATUS_CONFIG: Record<Dispatch['status'], { label: string; icon: typeof CheckCircle; color: string }> = {
  concluido: { label: 'Concluído', icon: CheckCircle, color: 'text-green-600' },
  parcial:   { label: 'Parcial',   icon: AlertCircle, color: 'text-amber-600' },
  falhou:    { label: 'Falhou',    icon: XCircle,      color: 'text-red-600' },
};

const DisparosTab: React.FC = () => {
  const [templates, setTemplates] = useState<WhatsappTemplate[]>([]);
  const [historico, setHistorico] = useState<Dispatch[]>([]);
  const [loading, setLoading] = useState(true);
  const [templateSelecionado, setTemplateSelecionado] = useState('');
  const [destinatarios, setDestinatarios] = useState('');
  const [enviando, setEnviando] = useState(false);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [tpls, hist] = await Promise.all([
        apiClient.get<WhatsappTemplate[]>('/whatsapp-agent/templates'),
        apiClient.get<Dispatch[]>('/whatsapp-agent/disparos'),
      ]);
      setTemplates(tpls ?? []);
      setHistorico(hist ?? []);
    } catch {
      setTemplates([]);
      setHistorico([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const handleEnviar = async () => {
    const recipients = destinatarios.split('\n').map((n) => n.trim()).filter(Boolean);
    if (!templateSelecionado || recipients.length === 0) {
      alert('Selecione um template e informe pelo menos um destinatário');
      return;
    }
    setEnviando(true);
    try {
      await apiClient.post('/whatsapp-agent/disparos', { templateName: templateSelecionado, recipients });
      setDestinatarios('');
      await fetchAll();
    } catch (err: any) {
      alert(err?.message || 'Erro ao enviar disparo');
    } finally {
      setEnviando(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-2xl border border-gray-200 p-6">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-black text-gray-900 mb-1 flex items-center gap-3">
              <Send className="w-6 h-6 text-blue-600" /> Disparos
            </h2>
            <p className="text-gray-600">Campanhas em massa com templates aprovados pela Meta (seção 8.3)</p>
          </div>
          <button onClick={fetchAll}
            className="flex items-center gap-2 px-4 py-2 rounded-xl border-2 border-gray-300 text-gray-700 font-bold hover:bg-gray-50">
            <RefreshCw className="w-5 h-5" />
          </button>
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-20"><Loader2 className="w-10 h-10 text-blue-600 animate-spin" /></div>
      ) : (
        <>
          {templates.length === 0 ? (
            <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6 flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-bold text-amber-900">Nenhum template aprovado ainda</p>
                <p className="text-sm text-amber-800 mt-1">
                  Templates de WhatsApp precisam ser submetidos e aprovados no Meta Business Manager antes de
                  poderem ser usados aqui — é uma dependência externa (seção "O que ainda falta da Federação").
                  Assim que houver templates aprovados, eles aparecem automaticamente nesta lista.
                </p>
              </div>
            </div>
          ) : (
            <div className="bg-white rounded-2xl border border-gray-200 p-6 space-y-4">
              <h3 className="font-black text-gray-900">Novo disparo</h3>
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-2">Template</label>
                <select value={templateSelecionado} onChange={(e) => setTemplateSelecionado(e.target.value)}
                  className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl text-gray-900 font-semibold">
                  <option value="">Selecione um template</option>
                  {templates.map((t) => (
                    <option key={t.name} value={t.name}>{t.name} — {t.description}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-2">
                  Destinatários <span className="text-gray-400 font-normal">(um número por linha, com DDI)</span>
                </label>
                <textarea value={destinatarios} onChange={(e) => setDestinatarios(e.target.value)} rows={5}
                  placeholder={'5511999999999\n5511988888888'}
                  className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl text-gray-900 font-mono text-sm resize-none" />
              </div>
              <button onClick={handleEnviar} disabled={enviando}
                className="flex items-center gap-2 px-5 py-3 rounded-xl bg-blue-600 text-white font-bold hover:bg-blue-700 disabled:opacity-50">
                {enviando ? <Loader2 className="w-5 h-5 animate-spin" /> : <Send className="w-5 h-5" />} Enviar disparo
              </button>
            </div>
          )}

          <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
            <div className="p-5 border-b border-gray-200">
              <h3 className="font-black text-gray-900">Histórico de disparos</h3>
            </div>
            {historico.length === 0 ? (
              <p className="text-center text-gray-400 py-10">Nenhum disparo realizado ainda</p>
            ) : (
              <div className="divide-y divide-gray-100">
                {historico.map((d) => {
                  const st = STATUS_CONFIG[d.status];
                  const Icon = st.icon;
                  return (
                    <div key={d.id} className="p-4 flex items-center justify-between">
                      <div>
                        <p className="font-bold text-gray-900">{d.templateName}</p>
                        <p className="text-xs text-gray-500">
                          {d.sentCount} enviados · {d.failedCount} falharam · {d.recipients.length} destinatários
                        </p>
                      </div>
                      <span className={`flex items-center gap-1 text-sm font-bold ${st.color}`}>
                        <Icon className="w-4 h-4" /> {st.label}
                      </span>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default DisparosTab;
